import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TrackingApiService } from '../services/tracking-api.service';
import type { FailedQueryRow } from '../tracking-demo.models';

/** Every reason the backend can emit (call_audit.REASONS minus "ok"). */
const REASONS = ['agent_silent', 'answered_without_retrieval', 'ignored_retrieved_context',
                 'ungrounded_claim', 'contradicted_claim', 'kb_gap', 'stale_source',
                 'retrieval_misfire', 'deflected', 'partial_answer'];

/**
 * The Failed queries tab — every failed/unanswered prospect query across recent
 * audited calls, filterable by reason/severity, exportable as JSONL training data.
 * Self-fetching: reads audit/failed-queries via the shared TrackingApiService.
 */
@Component({
  selector: 'app-failed-queries',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
      <div class="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <select class="rounded-md border px-2 py-1 text-xs" [ngModel]="reason()"
                (ngModelChange)="reason.set($event); load()">
          <option value="">every reason</option>
          @for (r of reasons; track r) { <option [value]="r">{{ r }}</option> }
        </select>
        <select class="rounded-md border px-2 py-1 text-xs" [ngModel]="severity()"
                (ngModelChange)="severity.set($event); load()">
          <option value="">every severity</option>
          <option value="high">high</option>
          <option value="medium">medium</option>
          <option value="low">low</option>
        </select>
        <select class="rounded-md border px-2 py-1 text-xs" [ngModel]="days()"
                (ngModelChange)="days.set(+$event); load()">
          <option [value]="7">last 7 days</option>
          <option [value]="30">last 30 days</option>
          <option [value]="90">last 90 days</option>
        </select>
        <span class="text-xs text-gray-500">{{ total() }} failed quer{{ total() === 1 ? 'y' : 'ies' }}</span>
        <button class="ml-auto rounded-md border px-2 py-1 text-xs font-medium"
                (click)="export()">Export JSONL</button>
      </div>

      @if (loading()) { <div class="py-6 text-center text-sm text-gray-400">Loading…</div> }
      @else if (!rows().length) {
      <div class="py-6 text-center text-sm text-gray-400">No failed queries in this window.</div>
      } @else {
      <table class="w-full text-left text-sm">
        <thead>
          <tr class="text-[10px] uppercase tracking-wide text-gray-400">
            <th class="py-1 pr-2">When</th><th class="py-1 pr-2">Query</th>
            <th class="py-1 pr-2">Reason</th><th class="py-1 pr-2">Severity</th>
            <th class="py-1">Fix</th>
          </tr>
        </thead>
        <tbody>
          @for (r of rows(); track $index) {
          <tr class="border-t border-gray-100 align-top dark:border-line">
            <td class="py-2 pr-2 whitespace-nowrap text-xs text-gray-500">{{ r.endedAt | date: 'MMM d, HH:mm' }}</td>
            <td class="py-2 pr-2">
              <div class="font-medium">“{{ r.query }}”</div>
              @if (r.answer) { <div class="text-xs text-gray-500">→ {{ r.answer }}</div> }
            </td>
            <td class="py-2 pr-2 font-mono text-xs">{{ r.reason }}</td>
            <td class="py-2 pr-2">
              <span class="rounded px-1.5 py-0.5 text-[10px] font-medium"
                    [class]="r.severity === 'high' ? 'bg-rose-100 text-rose-700'
                             : r.severity === 'medium' ? 'bg-amber-100 text-amber-700'
                             : 'bg-gray-100 text-gray-600'">{{ r.severity }}</span>
            </td>
            <td class="py-2 text-xs text-gray-600">
              @if (r.fix?.text) { {{ r.fix?.text }} } @else { — }
            </td>
          </tr>
          }
        </tbody>
      </table>
      @if (total() > pageSize) {
      <div class="mt-3 flex items-center gap-2 text-xs">
        <button class="rounded border px-2 py-1" [disabled]="page() <= 1"
                (click)="page.set(page() - 1); load()">Prev</button>
        <span>page {{ page() }} / {{ pages() }}</span>
        <button class="rounded border px-2 py-1" [disabled]="page() >= pages()"
                (click)="page.set(page() + 1); load()">Next</button>
      </div>
      }
      }
    </div>
  `,
})
export class FailedQueriesComponent implements OnInit {
  private readonly api = inject(TrackingApiService);
  readonly reasons = REASONS;
  readonly pageSize = 50;

  readonly rows = signal<FailedQueryRow[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly days = signal(30);
  readonly reason = signal('');
  readonly severity = signal('');
  readonly loading = signal(false);

  ngOnInit(): void {
    void this.load();
  }

  pages(): number {
    return Math.max(1, Math.ceil(this.total() / this.pageSize));
  }

  async load(): Promise<void> {
    this.loading.set(true);
    try {
      const res = await this.api.getFailedQueries({
        days: this.days(), reason: this.reason() || undefined,
        severity: this.severity() || undefined,
        page: this.page(), pageSize: this.pageSize,
      });
      this.rows.set(res.items);
      this.total.set(res.total);
    } catch {
      this.rows.set([]);
      this.total.set(0);
    } finally {
      this.loading.set(false);
    }
  }

  async export(): Promise<void> {
    try {
      const text = await this.api.exportFailedQueries(this.days());
      const url = URL.createObjectURL(new Blob([text], { type: 'application/x-ndjson' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'failed-queries.jsonl';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* toaster lives at the shell; a silent no-op beats a broken tab */
    }
  }
}
